import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import { Bar, Line } from "react-chartjs-2";
import type { ChartOptions } from "chart.js";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ShareIcon from "@mui/icons-material/Share";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignIcon from "@mui/icons-material/Campaign";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CreateCampaignLinkDialog, {
  buildCampaignLink,
} from "../components/CreateCampaignLinkDialog";
import { copyToClipboard, useSnackbar } from "../hooks/useSnackbar";
import DashboardStatsItem from "../components/dashboard-stats-item";
import axios from "../utils/axios-instance";
import { getToken } from "../utils";
import { ServerRoutes } from "../routes";
import {
  CampaignDetailResponse,
  CampaignStat,
  CampaignStatsResponse,
} from "../types/campaign-stats.type";

const MAIN_CAMPAIGN = "main_channel";

type Preset = "all" | "today" | "7d" | "30d" | "launch" | "custom";

// Distinct, colour-blind friendly series colours; main_channel always gets navy.
const SERIES_COLORS = [
  "#E69F00",
  "#009E73",
  "#CC79A7",
  "#56B4E9",
  "#D55E00",
  "#F0E442",
  "#0072B2",
  "#999999",
];

const numberFormat = new Intl.NumberFormat("en-US");
const fmt = (value: number) => numberFormat.format(value);
const fmtMoney = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
const pct = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 1000) / 10 : 0;
const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("MMM DD, YYYY HH:mm") : "—";
const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("MMM DD, YYYY") : "—";

const buildQuery = (from: Dayjs | null, to: Dayjs | null) => {
  const query = new URLSearchParams();
  if (from) query.append("from", from.format("YYYY-MM-DD"));
  if (to) query.append("to", to.format("YYYY-MM-DD"));
  return query.toString();
};

const fetchStats = async (from: Dayjs | null, to: Dayjs | null) => {
  const response = await axios.get<CampaignStatsResponse>(
    `${ServerRoutes.campaignStats}?${buildQuery(from, to)}`,
  );
  return response.data;
};

const fetchDetail = async (
  id: number,
  from: Dayjs | null,
  to: Dayjs | null,
) => {
  const response = await axios.get<CampaignDetailResponse>(
    `${ServerRoutes.campaigns}/${id}/stats?${buildQuery(from, to)}`,
  );
  return response.data;
};

const noDataLabels = { datalabels: { display: false } } as const;

function SectionCard({
  title,
  subtitle,
  children,
  action,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
      {children}
    </Card>
  );
}

/** One horizontal bar of the activation funnel, relative to total signups. */
function FunnelRow({
  label,
  value,
  total,
  hint,
}: {
  label: string;
  value: number;
  total: number;
  hint?: string;
}) {
  const rate = pct(value, total);
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={0.5} gap={2}>
        <Tooltip title={hint ?? ""} placement="top-start">
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
        </Tooltip>
        <Typography variant="body2" color="text.secondary" noWrap>
          {fmt(value)} · {rate}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(rate, 100)}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}

function MetricList({
  items,
}: {
  items: Array<{ label: string; value: React.ReactNode; hint?: string }>;
}) {
  return (
    <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
      {items.map((item) => (
        <Box
          key={item.label}
          display="flex"
          justifyContent="space-between"
          py={1}
          gap={2}
        >
          <Tooltip title={item.hint ?? ""} placement="top-start">
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
          </Tooltip>
          <Typography variant="body2" fontWeight={600} textAlign="right">
            {item.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

const gridSx = (min: number) => ({
  display: "grid",
  gap: 2,
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}px), 1fr))`,
  minWidth: 0,
});

export const CampaignStatsSection = () => {
  const navigate = useNavigate();
  if (!getToken()) navigate("/login");
  const theme = useTheme();

  const [preset, setPreset] = useState<Preset>("all");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hideMain, setHideMain] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const { notify, SnackbarHost } = useSnackbar();

  const copyLink = async (referralCode: string, campaignName: string) => {
    const ok = await copyToClipboard(buildCampaignLink(referralCode, campaignName));
    notify(ok ? "Campaign link copied" : "Could not copy link", ok ? "success" : "error");
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<
    CampaignStatsResponse,
    Error
  >({
    queryKey: ["campaignStats", fromDate?.format(), toDate?.format()],
    queryFn: () => fetchStats(fromDate, toDate),
    refetchOnWindowFocus: false,
  });

  const campaigns = useMemo(() => data?.campaigns ?? [], [data]);

  // Earliest campaign creation = when campaign tracking went live.
  const launchDate = useMemo(() => {
    const dates = campaigns.map((c) => dayjs(c.createdAt));
    return dates.length
      ? dates.reduce((min, d) => (d.isBefore(min) ? d : min))
      : null;
  }, [campaigns]);

  // Default the drill-down to the best non-main campaign once data arrives.
  useEffect(() => {
    if (!campaigns.length) return;
    if (selectedId && campaigns.some((c) => c.id === selectedId)) return;
    const preferred =
      [...campaigns]
        .filter((c) => c.name !== MAIN_CAMPAIGN)
        .sort((a, b) => b.users - a.users)[0] ?? campaigns[0];
    setSelectedId(preferred.id);
  }, [campaigns, selectedId]);

  const selected = campaigns.find((c) => c.id === selectedId) ?? null;

  const { data: detail, isFetching: detailLoading } = useQuery<
    CampaignDetailResponse,
    Error
  >({
    queryKey: [
      "campaignDetail",
      selectedId,
      fromDate?.format(),
      toDate?.format(),
    ],
    queryFn: () => fetchDetail(selectedId as number, fromDate, toDate),
    enabled: !!selectedId,
    refetchOnWindowFocus: false,
  });

  const applyPreset = (value: Preset | null) => {
    if (!value) return;
    setPreset(value);
    const today = dayjs();
    if (value === "all") {
      setFromDate(null);
      setToDate(null);
    } else if (value === "today") {
      setFromDate(today);
      setToDate(today);
    } else if (value === "7d") {
      setFromDate(today.subtract(6, "day"));
      setToDate(today);
    } else if (value === "30d") {
      setFromDate(today.subtract(29, "day"));
      setToDate(today);
    } else if (value === "launch" && launchDate) {
      setFromDate(launchDate);
      setToDate(null);
    }
  };

  const totals = useMemo(() => {
    const sum = (pick: (c: CampaignStat) => number) =>
      campaigns.reduce((acc, c) => acc + pick(c), 0);
    const nonMain = campaigns.filter((c) => c.name !== MAIN_CAMPAIGN);
    return {
      users: sum((c) => c.users),
      today: sum((c) => c.today),
      last7: sum((c) => c.last7Days),
      campaignUsers: nonMain.reduce((acc, c) => acc + c.users, 0),
      activeCampaigns: nonMain.filter((c) => c.users > 0).length,
      campaignCount: nonMain.length,
      watchers: sum((c) => c.activation.watchers),
      invited: sum((c) => c.referrals.invitedUsers),
    };
  }, [campaigns]);

  const colorFor = (campaign: CampaignStat, index: number) =>
    campaign.name === MAIN_CAMPAIGN
      ? theme.palette.primary.main
      : SERIES_COLORS[index % SERIES_COLORS.length];

  const dailyChart = useMemo(() => {
    const visible = campaigns
      .map((c, index) => ({ c, color: colorFor(c, index) }))
      .filter(({ c }) => !(hideMain && c.name === MAIN_CAMPAIGN));
    return {
      labels: (data?.labels ?? []).map((d) => dayjs(d).format("MMM DD")),
      datasets: visible.map(({ c, color }) => ({
        label: c.name,
        data: c.daily,
        borderColor: color,
        backgroundColor: alpha(color, 0.15),
        tension: 0.3,
        pointRadius: 2,
        fill: false,
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, data, hideMain, theme.palette.primary.main]);

  const shareChart = useMemo(() => {
    const visible = campaigns
      .map((c, index) => ({ c, color: colorFor(c, index) }))
      .filter(({ c }) => !(hideMain && c.name === MAIN_CAMPAIGN));
    return {
      labels: visible.map(({ c }) => c.name),
      datasets: [
        {
          label: "Signups",
          data: visible.map(({ c }) => c.users),
          backgroundColor: visible.map(({ color }) => alpha(color, 0.75)),
          borderRadius: 4,
          maxBarThickness: 40,
        },
        {
          label: "Watched a course",
          data: visible.map(({ c }) => c.activation.watchers),
          backgroundColor: visible.map(({ color }) => alpha(color, 0.35)),
          borderRadius: 4,
          maxBarThickness: 40,
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, hideMain, theme.palette.primary.main]);

  const lineOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { position: "top" }, ...noDataLabels },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const barOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" }, ...noDataLabels },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const detailDailyChart = detail && {
    labels: detail.labels.map((d) => dayjs(d).format("MMM DD")),
    datasets: [
      {
        label: "Signups",
        data: detail.daily,
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        tension: 0.3,
        pointRadius: 2,
        fill: true,
      },
    ],
  };

  const detailHourlyChart = detail && {
    labels: detail.hourly.map((_, hour) => `${String(hour).padStart(2, "0")}`),
    datasets: [
      {
        label: "Signups by hour (UTC)",
        data: detail.hourly,
        backgroundColor: alpha(theme.palette.primary.main, 0.7),
        borderRadius: 4,
        maxBarThickness: 24,
      },
    ],
  };

  const rangeLabel =
    fromDate || toDate
      ? `${fromDate ? fromDate.format("MMM DD, YYYY") : "Beginning"} → ${
          toDate ? toDate.format("MMM DD, YYYY") : "Now"
        }`
      : "All time";

  return (
    <Box display="flex" flexDirection="column" gap={3} sx={{ minWidth: 0 }}>
      {/* Header + filters */}
      <Card sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.secondary">
              Campaign Stats
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Signups are attributed from startapp links in the form
              REFCODE_campaign. Range: {rangeLabel} (UTC)
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddLinkIcon />}
              onClick={() => setLinkDialogOpen(true)}
            >
              New campaign link
            </Button>
          </Box>
        </Box>

        <Box
          display="flex"
          gap={2}
          mt={2.5}
          flexWrap="wrap"
          alignItems="center"
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, value) => applyPreset(value)}
            sx={{ flexWrap: "wrap" }}
          >
            <ToggleButton value="all">All time</ToggleButton>
            <ToggleButton value="launch" disabled={!launchDate}>
              Since launch
            </ToggleButton>
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="7d">7 days</ToggleButton>
            <ToggleButton value="30d">30 days</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>
          {preset === "custom" && (
            <>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={(value) => setFromDate(value)}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="To"
                value={toDate}
                onChange={(value) => setToDate(value)}
                slotProps={{ textField: { size: "small" } }}
              />
            </>
          )}
        </Box>
      </Card>

      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.message || "Failed to load campaign stats."}
        </Alert>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Alert severity="info" variant="outlined">
            <b>{MAIN_CAMPAIGN}</b> is the default campaign: it holds every user
            who joined without a campaign link, including all users from before
            campaign tracking started
            {launchDate ? ` (${launchDate.format("MMM DD, YYYY")})` : ""}. Use
            “Since launch” for a fair comparison.
          </Alert>

          {/* Headline KPIs */}
          <Box sx={gridSx(220)}>
            <DashboardStatsItem
              title="Total signups"
              description={fmt(totals.users)}
              detail={rangeLabel}
              icon={PeopleAltIcon}
            />
            <DashboardStatsItem
              title="Via campaign links"
              description={fmt(totals.campaignUsers)}
              detail={`${pct(totals.campaignUsers, totals.users)}% of signups`}
              icon={CampaignIcon}
            />
            <DashboardStatsItem
              title="Campaigns"
              description={fmt(totals.campaignCount)}
              detail={`${totals.activeCampaigns} with signups in range`}
              icon={TrendingUpIcon}
            />
            <DashboardStatsItem
              title="Signups today"
              description={fmt(totals.today)}
              detail={`${fmt(totals.last7)} in the last 7 days`}
              icon={TodayIcon}
            />
            <DashboardStatsItem
              title="Watched a course"
              description={`${pct(totals.watchers, totals.users)}%`}
              detail={`${fmt(totals.watchers)} users`}
              icon={PlayCircleOutlineIcon}
            />
            <DashboardStatsItem
              title="Invited by these users"
              description={fmt(totals.invited)}
              detail="Second-generation signups"
              icon={ShareIcon}
            />
          </Box>

          {/* Comparison table */}
          <SectionCard
            title="Campaign comparison"
            subtitle="Click a row to open its detailed report below. Percentages are relative to the campaign's signups."
          >
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell>Campaign</TableCell>
                    <TableCell align="right">Signups</TableCell>
                    <TableCell align="right">Share</TableCell>
                    <TableCell align="right">Today</TableCell>
                    <TableCell align="right">7d</TableCell>
                    <TableCell align="right">30d</TableCell>
                    <TableCell align="right">Watched</TableCell>
                    <TableCell align="right">Level 1+</TableCell>
                    <TableCell align="right">Main ch.</TableCell>
                    <TableCell align="right">Exchange</TableCell>
                    <TableCell align="right">Invites / user</TableCell>
                    <TableCell>Last signup</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        No campaigns yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...campaigns]
                      .sort((a, b) => b.users - a.users)
                      .map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          selected={c.id === selectedId}
                          onClick={() => setSelectedId(c.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  flexShrink: 0,
                                  backgroundColor: colorFor(
                                    c,
                                    campaigns.indexOf(c),
                                  ),
                                }}
                              />
                              <Typography variant="body2" fontWeight={600}>
                                {c.name}
                              </Typography>
                              {c.name === MAIN_CAMPAIGN && (
                                <Chip label="default" size="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{fmt(c.users)}</TableCell>
                          <TableCell align="right">
                            {pct(c.users, totals.users)}%
                          </TableCell>
                          <TableCell align="right">{fmt(c.today)}</TableCell>
                          <TableCell align="right">
                            {fmt(c.last7Days)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt(c.last30Days)}
                          </TableCell>
                          <TableCell align="right">
                            {c.activation.watchRate}%
                          </TableCell>
                          <TableCell align="right">
                            {pct(c.activation.leveledUp, c.users)}%
                          </TableCell>
                          <TableCell align="right">
                            {pct(c.external.mainChannelMembers, c.users)}%
                          </TableCell>
                          <TableCell align="right">
                            {pct(c.activation.exchangeLinked, c.users)}%
                          </TableCell>
                          <TableCell align="right">
                            {c.referrals.viralCoefficient}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDateTime(c.lastSignupAt)}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDate(c.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>

          {/* Trend charts */}
          <SectionCard
            title="Daily signups by campaign"
            subtitle={
              data?.range.seriesFrom
                ? `${formatDate(data.range.seriesFrom)} → ${formatDate(
                    data.range.seriesTo,
                  )} (defaults to the last 30 days)`
                : undefined
            }
            action={
              <FormControlLabel
                control={
                  <Switch
                    checked={hideMain}
                    onChange={(e) => setHideMain(e.target.checked)}
                  />
                }
                label={`Hide ${MAIN_CAMPAIGN}`}
              />
            }
          >
            <Box sx={{ height: 320 }}>
              {dailyChart.datasets.length ? (
                <Line data={dailyChart} options={lineOptions} />
              ) : (
                <Typography color="text.secondary">
                  No campaigns to show.
                </Typography>
              )}
            </Box>
          </SectionCard>

          <SectionCard
            title="Signups vs. activated users"
            subtitle="Activated = watched at least one course episode"
          >
            <Box sx={{ height: 300 }}>
              <Bar data={shareChart} options={barOptions} />
            </Box>
          </SectionCard>

          {/* Drill-down */}
          {selected && (
            <>
              <Card sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {selected.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Created {formatDateTime(selected.createdAt)} · first
                      signup {formatDateTime(selected.firstSignupAt)} · last
                      signup {formatDateTime(selected.lastSignupAt)}
                    </Typography>
                  </Box>
                  <TextField
                    select
                    size="small"
                    label="Campaign"
                    value={selected.id}
                    onChange={(e) => setSelectedId(Number(e.target.value))}
                    sx={{ minWidth: 220 }}
                  >
                    {campaigns.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name} ({fmt(c.users)})
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Card>

              <Box sx={gridSx(200)}>
                <DashboardStatsItem
                  title="Signups"
                  description={fmt(selected.users)}
                  detail={`${pct(selected.users, totals.users)}% of all signups`}
                  icon={PeopleAltIcon}
                />
                <DashboardStatsItem
                  title="Last 7 days"
                  description={fmt(selected.last7Days)}
                  detail={`${fmt(selected.today)} today · ${fmt(
                    selected.last30Days,
                  )} in 30 days`}
                  icon={DateRangeIcon}
                />
                <DashboardStatsItem
                  title="Watch rate"
                  description={`${selected.activation.watchRate}%`}
                  detail={`${fmt(selected.activation.watchers)} watchers · ${
                    selected.activation.avgItemsPerWatcher
                  } episodes each`}
                  icon={PlayCircleOutlineIcon}
                />
                <DashboardStatsItem
                  title="Invites per user"
                  description={selected.referrals.viralCoefficient}
                  detail={`${fmt(selected.referrals.invitedUsers)} invited by ${fmt(
                    selected.referrals.inviters,
                  )} users`}
                  icon={ShareIcon}
                />
                <DashboardStatsItem
                  title="Peak hour (UTC)"
                  description={
                    detail && detail.hourly.some((v) => v > 0)
                      ? `${String(
                          detail.hourly.indexOf(Math.max(...detail.hourly)),
                        ).padStart(2, "0")}:00`
                      : "—"
                  }
                  detail="Hour with the most signups"
                  icon={ScheduleIcon}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    lg: "repeat(2, minmax(0, 1fr))",
                  },
                }}
              >
                <SectionCard
                  title="Activation funnel"
                  subtitle="Share of this campaign's signups"
                >
                  <Stack gap={1.75}>
                    <FunnelRow
                      label="Signed up"
                      value={selected.users}
                      total={selected.users}
                    />
                    <FunnelRow
                      label="Watched a course"
                      value={selected.activation.watchers}
                      total={selected.users}
                      hint="Has at least one course watch-history record"
                    />
                    <FunnelRow
                      label="Reached level 1"
                      value={selected.activation.leveledUp}
                      total={selected.users}
                    />
                    <FunnelRow
                      label="Main channel member"
                      value={selected.external.mainChannelMembers}
                      total={selected.users}
                      hint="From the last membership check"
                    />
                    <FunnelRow
                      label="Exchange account linked"
                      value={selected.activation.exchangeLinked}
                      total={selected.users}
                    />
                    <FunnelRow
                      label="Email verified"
                      value={selected.activation.emailVerified}
                      total={selected.users}
                    />
                    <FunnelRow
                      label="Invited someone"
                      value={selected.referrals.inviters}
                      total={selected.users}
                    />
                  </Stack>
                </SectionCard>

                <SectionCard
                  title="Referrals & transactions"
                  subtitle="Accepted transactions of this campaign's users"
                >
                  <MetricList
                    items={[
                      {
                        label: "Signed up with a referrer",
                        value: `${fmt(selected.referrals.withReferrer)} · ${pct(
                          selected.referrals.withReferrer,
                          selected.users,
                        )}%`,
                      },
                      {
                        label: "Distinct referrers",
                        value: fmt(selected.referrals.distinctReferrers),
                        hint: "Different people whose link brought users in",
                      },
                      {
                        label: "Users they invited",
                        value: fmt(selected.referrals.invitedUsers),
                      },
                      {
                        label: "Deposits",
                        value: `${fmt(
                          selected.transactions.deposit.count,
                        )} · ${fmtMoney(selected.transactions.deposit.amount)}`,
                      },
                      {
                        label: "Purchases",
                        value: `${fmt(
                          selected.transactions.buy.count,
                        )} · ${fmtMoney(selected.transactions.buy.amount)}`,
                      },
                      {
                        label: "Paying users",
                        value: fmt(selected.transactions.buy.users),
                      },
                      {
                        label: "Referral rewards",
                        value: `${fmt(
                          selected.transactions.referralReward.count,
                        )} · ${fmtMoney(
                          selected.transactions.referralReward.amount,
                        )}`,
                      },
                    ]}
                  />
                </SectionCard>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 1fr)" },
                }}
              >
                <SectionCard title={`Daily signups — ${selected.name}`}>
                  <Box sx={{ height: 280 }}>
                    {detailDailyChart ? (
                      <Line data={detailDailyChart} options={lineOptions} />
                    ) : (
                      <CircularProgress size={28} />
                    )}
                  </Box>
                </SectionCard>
                <SectionCard title="Signups by hour (UTC)">
                  <Box sx={{ height: 280 }}>
                    {detailHourlyChart ? (
                      <Bar
                        data={detailHourlyChart}
                        options={{
                          ...barOptions,
                          plugins: { legend: { display: false }, ...noDataLabels },
                        }}
                      />
                    ) : (
                      <CircularProgress size={28} />
                    )}
                  </Box>
                </SectionCard>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) minmax(0, 1.4fr)" },
                }}
              >
                <SectionCard
                  title="Top referrers"
                  subtitle="Whose links brought users in through this campaign"
                >
                  <Paper variant="outlined" sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "action.hover" }}>
                          <TableCell>Referrer</TableCell>
                          <TableCell>Invite code</TableCell>
                          <TableCell align="right">Signups</TableCell>
                          <TableCell align="right">Level 1+</TableCell>
                          <TableCell align="right">Link</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!detail || detailLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <CircularProgress size={20} />
                            </TableCell>
                          </TableRow>
                        ) : detail.topReferrers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No referrers in this range.
                            </TableCell>
                          </TableRow>
                        ) : (
                          detail.topReferrers.map((r) => (
                            <TableRow
                              key={r.id}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() => navigate(`/referrals/${r.id}`)}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {r.username
                                    ? `@${r.username}`
                                    : r.firstName || `User ${r.id}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  #{r.id}
                                </Typography>
                              </TableCell>
                              <TableCell>{r.inviteCode || "—"}</TableCell>
                              <TableCell align="right">
                                {fmt(r.signups)}
                              </TableCell>
                              <TableCell align="right">
                                {fmt(r.activated)}
                              </TableCell>
                              <TableCell align="right">
                                {r.inviteCode &&
                                selected.name !== MAIN_CAMPAIGN ? (
                                  <Tooltip title="Copy this referrer's campaign link">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyLink(r.inviteCode!, selected.name);
                                      }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </SectionCard>

                <SectionCard
                  title="Latest signups"
                  subtitle="Most recent 50 users from this campaign"
                >
                  <Paper
                    variant="outlined"
                    sx={{ overflow: "auto", maxHeight: 440 }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Telegram ID</TableCell>
                          <TableCell align="right">Episodes</TableCell>
                          <TableCell align="right">Level</TableCell>
                          <TableCell>Signed up</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!detail || detailLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <CircularProgress size={20} />
                            </TableCell>
                          </TableRow>
                        ) : detail.recentUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No signups in this range.
                            </TableCell>
                          </TableRow>
                        ) : (
                          detail.recentUsers.map((u) => (
                            <TableRow
                              key={u.id}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() => navigate(`/user-tree/${u.id}`)}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {u.username
                                    ? `@${u.username}`
                                    : u.firstName || `User ${u.id}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  #{u.id}
                                </Typography>
                              </TableCell>
                              <TableCell>{u.telegramId}</TableCell>
                              <TableCell align="right">
                                {fmt(u.watchedItems)}
                              </TableCell>
                              <TableCell align="right">{u.level}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {formatDateTime(u.createdAt)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </SectionCard>
              </Box>
            </>
          )}
        </>
      )}
      <CreateCampaignLinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onCreated={(campaignId) => {
          // Select only after the refetch, or the default-selection effect
          // would replace an id that is not in the list yet.
          refetch().then(() => setSelectedId(campaignId));
        }}
      />
      <SnackbarHost />
    </Box>
  );
};
